import { selectionToMarkdown } from "@Utils/doc/replaceSelection";
import { useEffect, useRef, useState } from "react";
const getSelectedPlainText = async ()=>{
    try {
        let plainText = "";
        await Word.run(async (context)=>{
            const selection = context.document.getSelection();
            selection.load("text");
            await context.sync();
            plainText = selection.text;
        });
        return plainText;
    } catch (error) {
        return "";
    }
};
function removeCssComment(input) {
    return input.replace(/<!--[\s\S]*?-->/g, "").trim();
}
const updateText = async (isMounted, lastPlainTextRef, setSelectedText)=>{
    const plainText = await getSelectedPlainText();
    const normalizedPlainText = plainText.trim().replace(/\s+/g, " ");
    // Only update state if the plain text has actually changed
    if (isMounted && normalizedPlainText !== lastPlainTextRef.current) {
        // Only convert to markdown if there's actual text
        const markdown = plainText.trim() ? await selectionToMarkdown() : "";
        setSelectedText(removeCssComment(markdown));
        lastPlainTextRef.current = normalizedPlainText;
    }
};
// Trailing debounce: a click-drag selection fires DocumentSelectionChanged many
// times; we only care about the final selection and want to avoid flooding the
// Office bridge with Word.run round-trips.
const SELECTION_DEBOUNCE_MS = 250;
export const useWordSelection = ()=>{
    const [selectedText, setSelectedText] = useState("");
    const lastPlainTextRef = useRef("");
    useEffect(()=>{
        let isMounted = true;
        let debounceTimer;
        const runUpdate = ()=>updateText(isMounted, lastPlainTextRef, setSelectedText);
        const debouncedUpdate = ()=>{
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(()=>{
                if (isMounted) runUpdate();
            }, SELECTION_DEBOUNCE_MS);
        };
        // Initial read
        runUpdate();
        // Fallback trigger: re-read when the pane regains focus
        window.addEventListener("focus", debouncedUpdate);
        // Primary trigger: live selection changes inside the Word document. Without
        // this the badge only refreshed when the user clicked back into the pane.
        let selectionHandlerRegistered = false;
        Office.onReady(()=>{
            if (!isMounted) return;
            try {
                Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, debouncedUpdate, (result)=>{
                    if (result.status !== Office.AsyncResultStatus.Succeeded) {
                        console.warn("Failed to register DocumentSelectionChanged handler:", result.error);
                    }
                });
                // Mark registered synchronously so cleanup still detaches the handler
                // even if the component unmounts before the async callback resolves.
                selectionHandlerRegistered = true;
            } catch (error) {
                // Older Word hosts may not support DocumentSelectionChanged; the focus
                // fallback above keeps the previous behaviour working.
                console.warn("Failed to register DocumentSelectionChanged handler:", error);
            }
        });
        return ()=>{
            isMounted = false;
            if (debounceTimer) clearTimeout(debounceTimer);
            window.removeEventListener("focus", debouncedUpdate);
            if (selectionHandlerRegistered) {
                // Must pass the SAME function reference to detach the correct handler.
                Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
                    handler: debouncedUpdate
                }, (result)=>{
                    if (result.status !== Office.AsyncResultStatus.Succeeded) {
                        console.warn("Failed to remove DocumentSelectionChanged handler:", result.error);
                    }
                });
            }
        };
    }, []);
    return selectedText;
};
export async function getDocumentFormat() {
    try {
        return await new Promise((resolve)=>{
            Word.run(async (context)=>{
                const documentProperties = context.document.properties;
                documentProperties.load("title");
                await context.sync();
                // Check if we can access modern Word features (indicates docx)
                try {
                    const contentControls = context.document.contentControls;
                    contentControls.load("length");
                    await context.sync();
                    resolve("docx");
                } catch  {
                    resolve("doc");
                }
            }).catch(()=>resolve("unknown"));
        });
    } catch  {
        return "unknown";
    }
}
export async function getCurrentDocumentAsBlob() {
    const getFile = await new Promise((resolve, reject)=>Office.context.document.getFileAsync(Office.FileType.Compressed, {
            sliceSize: 65536
        }, (result)=>result.status === Office.AsyncResultStatus.Succeeded ? resolve(result.value) : reject(result.error)));
    const file = getFile;
    const sliceCount = file.sliceCount;
    const dataBuffer = new Uint8Array(file.size);
    let offset = 0;
    // 2) Read all slices in sequence
    for(let i = 0; i < sliceCount; i++){
        const slice = await new Promise((res, rej)=>{
            file.getSliceAsync(i, (s)=>s.status === Office.AsyncResultStatus.Succeeded ? res(s.value) : rej(s.error));
        });
        const chunk = new Uint8Array(slice.data);
        dataBuffer.set(chunk, offset);
        offset += chunk.byteLength;
    }
    file.closeAsync();
    const documentFormat = await getDocumentFormat();
    if (documentFormat === "unknown") {
        throw new Error("Unable to determine document format. Please ensure you are using a supported version of Word.");
    }
    const mimeType = documentFormat === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/msword";
    // Only use the filled portion of the buffer
    const filledBuffer = dataBuffer.slice(0, offset);
    const blob = new Blob([
        filledBuffer
    ], {
        type: mimeType
    });
    return {
        blob,
        fileType: documentFormat
    };
}
